// client/src/components/Admin/PlanesEducaCmp/_shared/PlanFormModal.js
//
// M07 - Modal de crear / editar plan. Reemplaza al PlanesForm legacy
// de Semantic UI + Formik.
//
// CONTRATO BACKEND (intocable - lo consume tambien Educa360-Registro):
// - Multipart con 14 campos del modelo PlanEduca + 'file' opcional para
//   la miniatura.
// - PlanesAPI.createPlan(accessToken, idPermission, data)
// - PlanesAPI.updatePlan(accessToken, idPermission, planId, data)
// - data lleva `file` (File) si hay nueva miniatura - la PlanesAPI lo
//   renombra internamente a `miniature` antes del fetch.
// - account: 'free' | 'premium' | 'promocion' (literales del backend).

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Row,
  Col,
  Button,
  message,
} from "antd";
import { PictureOutlined } from "@ant-design/icons";
import { PlanesAPI } from "../../../../api";
import { useAuth } from "../../../../hooks";
import styles from "./PlanFormModal.module.scss";

const planesController = new PlanesAPI();

const ACCOUNT_OPTIONS = [
  { value: "free", label: "Free" },
  { value: "premium", label: "Premium" },
  { value: "promocion", label: "Promocion" },
];

const SWITCH_FIELDS = [
  { name: "educamaker", label: "Educamaker" },
  { name: "curseBasic", label: "Formacion de iniciacion" },
  { name: "curseCertification", label: "Formacion de certificacion" },
  { name: "support", label: "Soporte" },
  { name: "publicity", label: "Publicidad" },
];

function defaultValues(plan) {
  return {
    title: plan?.title || "",
    description: plan?.description || "",
    account: plan?.account || "free",
    vrHeadsetNumber: plan?.vrHeadsetNumber ?? 0,
    vrHeadsetModel: plan?.vrHeadsetModel || "",
    bagModel: plan?.bagModel || "",
    accessTime: plan?.accessTime ?? 0,
    teacherNumber: plan?.teacherNumber ?? 0,
    studentsNumber: plan?.studentsNumber ?? 0,
    educamaker: !!plan?.educamaker,
    curseBasic: !!plan?.curseBasic,
    curseCertification: !!plan?.curseCertification,
    support: !!plan?.support,
    publicity: !!plan?.publicity,
    price: plan?.price ?? 0,
  };
}

export function PlanFormModal({ open, onClose, onSaved, plan }) {
  const [form] = Form.useForm();
  const { accessToken, user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const isEdit = !!plan?._id;
  const title = isEdit
    ? `Editar plan: ${plan.title || ""}`
    : "Crear nuevo plan";

  // Cuando se abre el modal o cambia el plan, reseteamos los campos.
  useEffect(() => {
    if (!open) return;
    form.setFieldsValue(defaultValues(plan));
    setFile(null);
    setPreviewUrl(plan?.miniature || null);
  }, [open, plan, form]);

  // Limpieza de URL.createObjectURL para no leakear memoria.
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFile = (selected) => {
    if (!selected) return;
    const validTypes = ["image/jpeg", "image/png"];
    if (!validTypes.includes(selected.type)) {
      message.error("Solo se admiten imagenes JPG o PNG.");
      return;
    }
    if (previewUrl && previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }
    setFile(selected);
    setPreviewUrl(URL.createObjectURL(selected));
  };

  const onPickFile = () => fileInputRef.current?.click();

  const onDrop = (e) => {
    e.preventDefault();
    const dropped = e.dataTransfer?.files?.[0];
    if (dropped) handleFile(dropped);
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      // Aplanamos al shape esperado por PlanesAPI (data + file opcional).
      const payload = { ...values };
      if (file) payload.file = file;

      const idPermission = user?.user?._id;
      if (!idPermission) {
        message.error("No se pudo obtener el ID del admin para esta peticion.");
        setSubmitting(false);
        return;
      }

      if (isEdit) {
        await planesController.updatePlan(
          accessToken,
          idPermission,
          plan._id,
          payload,
        );
        message.success("Plan actualizado correctamente.");
      } else {
        await planesController.createPlan(accessToken, idPermission, payload);
        message.success("Plan creado correctamente.");
      }

      onSaved?.();
      onClose?.();
    } catch (err) {
      // Si validateFields falla, no mostramos toast (los errores quedan inline).
      if (!err?.errorFields) {
        const msg =
          err?.message || err?.body?.message || "Error guardando el plan.";
        message.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues = useMemo(() => defaultValues(plan), [plan]);

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      width={720}
      destroyOnClose
      maskClosable={false}
      okText={isEdit ? "Actualizar plan" : "Crear plan"}
      cancelText="Cancelar"
      onOk={onSubmit}
      okButtonProps={{ loading: submitting }}
      className={styles.formModal}
    >
      <div className={styles.uploadRow}>
        <button
          type="button"
          className={styles.preview}
          onClick={onPickFile}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          aria-label="Seleccionar miniatura"
        >
          {previewUrl ? (
            <img src={previewUrl} alt="Miniatura del plan" />
          ) : (
            <PictureOutlined className={styles.placeholder} />
          )}
        </button>
        <div className={styles.uploadInfo}>
          <div className={styles.infoTitle}>Miniatura</div>
          <div className={styles.infoHint}>
            Arrastra una imagen o haz clic para seleccionarla. Formatos: JPG o
            PNG.
          </div>
          <Button size="small" onClick={onPickFile}>
            {previewUrl ? "Cambiar imagen" : "Subir imagen"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png"
            style={{ display: "none" }}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </div>
      </div>

      <Form form={form} layout="vertical" initialValues={initialValues}>
        <div className={styles.sectionTitle}>Datos generales</div>
        <Form.Item
          label="Titulo del plan"
          name="title"
          rules={[{ required: true, message: "El titulo es obligatorio" }]}
        >
          <Input placeholder="Ej. Plan Premium" maxLength={120} />
        </Form.Item>
        <Form.Item
          label="Descripcion"
          name="description"
          rules={[{ required: true, message: "La descripcion es obligatoria" }]}
        >
          <Input.TextArea
            rows={3}
            placeholder="Que incluye el plan, para quien es, propuesta de valor..."
            maxLength={1500}
            showCount
          />
        </Form.Item>
        <Form.Item label="Tipo de cuenta" name="account">
          <Select options={ACCOUNT_OPTIONS} />
        </Form.Item>

        <div className={styles.sectionTitle}>Equipamiento VR</div>
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item label="N de visores VR" name="vrHeadsetNumber">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Modelo de visor VR" name="vrHeadsetModel">
              <Input placeholder="Ej. Meta Quest 2" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Modelo de maleta" name="bagModel">
              <Input placeholder="Ej. TROLLEY" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="Meses de licencia"
          name="accessTime"
          rules={[{ required: true, message: "Indica los meses de licencia" }]}
        >
          <InputNumber min={0} style={{ width: 200 }} addonAfter="meses" />
        </Form.Item>

        <div className={styles.sectionTitle}>Capacidad</div>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="N de profesores"
              name="teacherNumber"
              rules={[
                { required: true, message: "Indica el numero de profesores" },
              ]}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="N de alumnos"
              name="studentsNumber"
              rules={[
                { required: true, message: "Indica el numero de alumnos" },
              ]}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <div className={styles.sectionTitle}>Servicios incluidos</div>
        <div className={styles.switchRow}>
          {SWITCH_FIELDS.map((sw) => (
            <Form.Item
              key={sw.name}
              name={sw.name}
              valuePropName="checked"
              noStyle
            >
              <SwitchRow label={sw.label} />
            </Form.Item>
          ))}
        </div>

        <div className={styles.sectionTitle}>Precio</div>
        <Form.Item
          label="Precio del plan"
          name="price"
          rules={[{ required: true, message: "Indica el precio" }]}
        >
          <InputNumber min={0} style={{ width: 220 }} addonAfter="EUR" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// SwitchRow encapsula el row visual del switch con su label, recibiendo
// el valor + onChange via clone que hace Form.Item interno de antd.
function SwitchRow({ label, value, onChange }) {
  return (
    <div className={styles.switchItem}>
      <span className={styles.switchLabel}>{label}</span>
      <Switch checked={!!value} onChange={onChange} />
    </div>
  );
}
